import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { NAVY } from '../utils/constants';
import { ArrowUp, ArrowDown, Trash2, Upload } from 'lucide-react';

// GalleryManager — ONE reusable photo-gallery manager used in two places inside
// Website Content: per cruise (owner_type 'cruise', owner_key = cruise_code) and
// per deck (owner_type 'deck', owner_key = the exact deck name from
// table_inventory). It reads/writes the media_gallery table and the public
// "site-media" Storage bucket directly — no edge functions. It touches no ticket
// logic and shares the surrounding Website Content / settings styling.

const BUCKET = 'site-media';
const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_BYTES = 5 * 1024 * 1024; // 5 MB
const EXT_BY_MIME = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp' };

// URL-safe folder slug for the storage path. owner_key may contain spaces
// (e.g. "Upper Deck"); the slug is only for the path — the DB owner_key column
// always stores the exact, unmodified key (the public-site contract).
const slug = (s) =>
  (s || '').toString().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const iconBtn = (disabled) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  padding: 0,
  border: '1px solid var(--color-border-strong)',
  borderRadius: 6,
  background: '#fff',
  color: NAVY,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.4 : 1,
});

export default function GalleryManager({ ownerType, ownerKey }) {
  const [images, setImages]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null); // { done, total }
  const [busyId, setBusyId]     = useState(null);  // row being reordered/removed
  const [confirmId, setConfirmId] = useState(null);
  const [msgs, setMsgs]         = useState([]);     // per-file upload feedback
  const [error, setError]       = useState('');
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error: err } = await supabase
      .from('media_gallery')
      .select('id, storage_path, sort_order')
      .eq('owner_type', ownerType)
      .eq('owner_key', ownerKey)
      .order('sort_order', { ascending: true });
    if (err) {
      setError(err.message);
      setImages([]);
    } else {
      setError('');
      setImages(
        (data || []).map((r) => ({
          ...r,
          url: supabase.storage.from(BUCKET).getPublicUrl(r.storage_path).data.publicUrl,
        }))
      );
    }
    setLoading(false);
  }, [ownerType, ownerKey]);

  useEffect(() => { load(); }, [load]);

  const busy = uploading || busyId !== null;

  // Next sort_order = max existing + 1, queried fresh so it stays correct even
  // if rows changed since the last load.
  const nextSortOrder = async () => {
    const { data } = await supabase
      .from('media_gallery')
      .select('sort_order')
      .eq('owner_type', ownerType)
      .eq('owner_key', ownerKey)
      .order('sort_order', { ascending: false })
      .limit(1);
    return (data && data[0] ? data[0].sort_order : -1) + 1;
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setError('');
    setMsgs([]);
    setUploading(true);

    // Validate FIRST — split into valid files and per-file rejection messages.
    const valid = [];
    const messages = [];
    files.forEach((f) => {
      if (!ACCEPTED.includes(f.type)) {
        messages.push({ name: f.name, ok: false, text: 'Skipped — only JPG, PNG or WebP images are allowed.' });
      } else if (f.size > MAX_BYTES) {
        messages.push({ name: f.name, ok: false, text: `Skipped — ${(f.size / 1024 / 1024).toFixed(1)} MB is over the 5 MB limit.` });
      } else {
        valid.push(f);
      }
    });

    if (!valid.length) {
      setMsgs(messages);
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
      return;
    }

    let nextSort = await nextSortOrder();
    setProgress({ done: 0, total: valid.length });
    let added = 0;

    for (let i = 0; i < valid.length; i++) {
      const f = valid[i];
      const ext = EXT_BY_MIME[f.type];
      const path = `${ownerType}/${slug(ownerKey)}/${crypto.randomUUID()}.${ext}`;

      // 1) Upload the file FIRST. If it fails, do not insert a row.
      const up = await supabase.storage.from(BUCKET).upload(path, f, {
        contentType: f.type,
        upsert: false,
      });
      if (up.error) {
        messages.push({ name: f.name, ok: false, text: `Upload failed — ${up.error.message}` });
        setProgress({ done: i + 1, total: valid.length });
        continue;
      }

      // 2) Insert the row. If it fails, delete the just-uploaded file so we
      //    never leave an orphan in storage.
      const ins = await supabase.from('media_gallery').insert({
        owner_type: ownerType,
        owner_key: ownerKey,
        storage_path: path,
        sort_order: nextSort,
      });
      if (ins.error) {
        await supabase.storage.from(BUCKET).remove([path]);
        messages.push({ name: f.name, ok: false, text: `Could not save — ${ins.error.message}` });
      } else {
        nextSort += 1;
        added += 1;
        messages.push({ name: f.name, ok: true, text: 'Uploaded.' });
      }
      setProgress({ done: i + 1, total: valid.length });
    }

    setMsgs(messages);
    setUploading(false);
    setProgress(null);
    if (fileRef.current) fileRef.current.value = '';
    if (added) await load();
  };

  // Reorder: swap sort_order with the neighbour and persist both immediately.
  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= images.length) return;
    const a = images[index];
    const b = images[target];
    setBusyId(a.id);
    setError('');
    const u1 = await supabase.from('media_gallery').update({ sort_order: b.sort_order }).eq('id', a.id);
    const u2 = await supabase.from('media_gallery').update({ sort_order: a.sort_order }).eq('id', b.id);
    setBusyId(null);
    if (u1.error || u2.error) {
      setError('Could not reorder. Please try again.');
    }
    await load();
  };

  // Remove: delete the file FIRST (so no orphaned files accumulate), then the
  // row. Any failure is surfaced; we never half-delete silently.
  const remove = async (img) => {
    setBusyId(img.id);
    setError('');
    const rm = await supabase.storage.from(BUCKET).remove([img.storage_path]);
    if (rm.error) {
      setBusyId(null);
      setConfirmId(null);
      setError(`Could not remove the image file — ${rm.error.message}`);
      return;
    }
    const del = await supabase.from('media_gallery').delete().eq('id', img.id);
    setBusyId(null);
    setConfirmId(null);
    if (del.error) {
      setError(`The file was removed but its record could not be deleted — ${del.error.message}. Try removing it again.`);
    }
    await load();
  };

  return (
    <div>
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => handleFiles(e.target.files)}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <button
          className="btn btn-outline btn-sm"
          onClick={() => fileRef.current && fileRef.current.click()}
          disabled={busy}
        >
          <Upload size={15} style={{ marginRight: 6 }} />
          {uploading ? 'Uploading…' : 'Add photos'}
        </button>
        {progress && (
          <span style={{ fontSize: '0.8rem', color: 'var(--color-ink-muted)' }}>
            {progress.done} / {progress.total}
          </span>
        )}
        <span style={{ fontSize: '0.72rem', color: 'var(--color-ink-faint)' }}>
          JPG, PNG or WebP · up to 5&nbsp;MB each
        </span>
      </div>

      {error && (
        <div className="toast toast-error" style={{ marginBottom: 12 }}>{error}</div>
      )}

      {msgs.length > 0 && (
        <ul style={{ listStyle: 'none', margin: '0 0 12px', padding: 0 }}>
          {msgs.map((m, i) => (
            <li
              key={i}
              style={{
                fontSize: '0.78rem',
                color: m.ok ? 'var(--color-success)' : 'var(--color-danger-strong)',
                marginBottom: 2,
              }}
            >
              <strong>{m.name}</strong> — {m.text}
            </li>
          ))}
        </ul>
      )}

      {loading ? (
        <p style={{ color: 'var(--color-ink-faint)', fontSize: '0.82rem', margin: 0 }}>Loading photos…</p>
      ) : images.length === 0 ? (
        <p style={{ color: 'var(--color-ink-faint)', fontSize: '0.82rem', margin: 0 }}>No photos yet.</p>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          {images.map((img, idx) => {
            const isConfirming = confirmId === img.id;
            const rowBusy = busyId === img.id;
            return (
              <div
                key={img.id}
                style={{
                  width: 116,
                  border: '1px solid var(--color-border)',
                  borderRadius: 8,
                  overflow: 'hidden',
                  background: '#fff',
                }}
              >
                <div style={{ position: 'relative', width: '100%', height: 88, background: 'var(--color-sunken)' }}>
                  <img
                    src={img.url}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                  <span
                    style={{
                      position: 'absolute', top: 4, left: 4,
                      fontSize: '0.62rem', fontWeight: 700, color: '#fff',
                      background: 'rgba(10,22,40,0.7)', borderRadius: 999,
                      padding: '1px 7px', lineHeight: 1.5,
                    }}
                  >
                    {idx + 1}
                  </span>
                </div>

                {isConfirming ? (
                  <div style={{ padding: 6, display: 'flex', gap: 6 }}>
                    <button
                      className="btn btn-danger btn-sm"
                      style={{ flex: 1, padding: '4px 0' }}
                      onClick={() => remove(img)}
                      disabled={rowBusy}
                    >
                      {rowBusy ? '…' : 'Delete'}
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ flex: 1, padding: '4px 0' }}
                      onClick={() => setConfirmId(null)}
                      disabled={rowBusy}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div style={{ padding: 6, display: 'flex', gap: 6, justifyContent: 'space-between' }}>
                    <button
                      style={iconBtn(busy || idx === 0)}
                      onClick={() => move(idx, -1)}
                      disabled={busy || idx === 0}
                      title="Move up"
                      aria-label="Move up"
                    >
                      <ArrowUp size={15} />
                    </button>
                    <button
                      style={iconBtn(busy || idx === images.length - 1)}
                      onClick={() => move(idx, 1)}
                      disabled={busy || idx === images.length - 1}
                      title="Move down"
                      aria-label="Move down"
                    >
                      <ArrowDown size={15} />
                    </button>
                    <button
                      style={{ ...iconBtn(busy), color: 'var(--color-danger-strong)' }}
                      onClick={() => setConfirmId(img.id)}
                      disabled={busy}
                      title="Remove"
                      aria-label="Remove"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
